import { useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { selectReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  CONVERSIONS_LABEL_LONG,
  compactCurrency,
  compactNum,
  currency,
  isConversionsPerPlatformScope,
  num,
} from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { useReportWidget } from '../hooks/useReportWidget';
import { type ReportingPermissions, useReportingPermissions } from '../hooks/useReportingPermissions';
import { UNKNOWN_BUCKET_KEY } from '../lib/publisherInventory';
import { COLORS, cardSx } from '../theme';
import BarChart from './BarChart';
import MetricToggle from './MetricToggle';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';

const KEY = 'topPublishersMetric';
const PAGE_SIZE = 10;

const CONVERSIONS_OPTION = { key: 'conversions', label: CONVERSIONS_LABEL_LONG };

const formatValue = (metric: string, value: unknown): string => {
  if (metric === 'spend' || metric === 'client_spend') return currency(value);
  return num(value);
};

/** Axis ticks use the compact form so this card reads like Top Campaigns. */
const formatTick = (metric: string, value: unknown): string => {
  if (metric === 'spend' || metric === 'client_spend') return compactCurrency(value);
  return compactNum(value);
};

/**
 * #4625 — Spend is offered only with "Show wholesale Spend and CPM", Client
 * Spend only with the Client Spend permission; Impressions always appears.
 * Default ranks by whichever money metric the user can actually see
 * (Client Spend when Spend is withheld), not always Impressions. Both
 * exported standalone so the decision is unit-testable without mounting the
 * card (see TopPublishersCard.test.ts).
 *
 * flank#4745 — Client Spend also needs `hasClientSpendData`: Bonneville-style
 * selections have the permission but no sold CPMs loaded yet, so ranking by
 * it would draw an empty $0–$1 chart.
 */
export const defaultMetric = ({
  canViewWholesaleSpend,
  canViewClientSpend,
  hasClientSpendData,
}: ReportingPermissions): string =>
  canViewWholesaleSpend ? 'impressions' : canViewClientSpend && hasClientSpendData ? 'client_spend' : 'impressions';

export const buildMetricOptions = (
  permissions: ReportingPermissions,
  perPlatform: boolean,
): { key: string; label: string }[] => {
  const base = [
    { key: 'impressions', label: 'Impressions' },
    ...(permissions.canViewWholesaleSpend ? [{ key: 'spend', label: 'Spend' }] : []),
    // flank#4745 — hidden, not shown-disabled, when the current selection has
    // no client spend data at all (see ReportingPermissions.hasClientSpendData).
    ...(permissions.canViewClientSpend && permissions.hasClientSpendData
      ? [{ key: 'client_spend', label: 'Client Spend' }]
      : []),
  ];
  return perPlatform ? [...base, CONVERSIONS_OPTION] : base;
};

/**
 * useReportingPermissions optimistically assumes Client Spend is allowed
 * until restrictedFields finishes loading. A manual pick made during that
 * window can turn out to be restricted once the real permissions resolve —
 * the guard must only protect a selection that's still valid, never one the
 * real permission check has since ruled out (flank#4650 CodeRabbit finding).
 */
export const reconcileMetric = (
  metric: string,
  userSelected: boolean,
  permissions: ReportingPermissions,
  perPlatform: boolean,
): string => {
  const stillAllowed = buildMetricOptions(permissions, perPlatform).some(o => o.key === metric);
  if (userSelected && stillAllowed) return metric;
  return defaultMetric(permissions);
};

/**
 * client_spend/client_cpm are computed server-side AFTER the SQL query runs
 * (enrichWithClientSpend) — not real queryable columns, so the backend hard-
 * rejects sorting by them (assertNoClientSpendSort, ribeye-api#2400). Sorting
 * by a computed metric has to happen here instead, once the rows come back.
 */
export const isComputedMetric = (metric: string): boolean => metric === 'client_spend' || metric === 'client_cpm';

/** Descending client-side rank by `metric`, applied before the rows are paged. */
export const sortByMetricDesc = (rows: ReportRow[], metric: string): ReportRow[] =>
  [...rows].sort((a, b) => Number(b[metric] ?? 0) - Number(a[metric] ?? 0));

/**
 * Ranks the Ribeye Publisher (`ribeye_publisher`, the Directus-backed rollup
 * flank#4643 added), not the raw DSP `publisher` name. Raw `publisher` is a
 * #4644 raw-inventory dimension: for anyone without `can_view_raw_inventory`
 * the API silently strips it, the query runs with no dimension at all, and
 * the one total row that comes back drew as a single unlabeled bar.
 */
export const PUBLISHER_DIMENSION = 'ribeye_publisher';

/**
 * Rows worth a bar. The `unknown` bucket is junk bundle values plus web
 * delivery that has no app at all — for GAM that's most of its traffic — so
 * charting it would make "Unknown" the top publisher. A row missing its key
 * entirely is the stripped-dimension total described above, never a publisher.
 */
export const chartablePublisherRows = (rows: ReportRow[]): ReportRow[] =>
  rows.filter(r => {
    const key = r[PUBLISHER_DIMENSION];
    return key !== null && key !== undefined && key !== '' && key !== UNKNOWN_BUCKET_KEY;
  });

/**
 * The CSV carries what the chart shows — the charted rows, publisher name
 * under the same `publisher` column it always had — not the rollup's internal
 * bucket key, logo URL and app count. Shared with ExportDialog, which reads
 * this widget's raw rows from the store.
 */
export const toExportRows = (rows: ReportRow[]): ReportRow[] =>
  chartablePublisherRows(rows).map(
    ({
      [PUBLISHER_DIMENSION]: _key,
      ribeye_publisher_name: name,
      ribeye_publisher_logo: _logo,
      app_count: _apps,
      ...metrics
    }) => ({ publisher: name ?? null, ...metrics }),
  );

const TopPublishersCard = () => {
  const permissions = useReportingPermissions();
  const [metric, setMetric] = useState(() => defaultMetric(permissions));
  const [page, setPage] = useState(0);
  const filters = useAppSelector(selectReportingV2Filters);
  // Tracks whether the viewer picked a metric themselves, so the permission
  // effect below only ever sets a DEFAULT — it must never clobber a manual
  // choice once restrictedFields finishes loading (see useReportingPermissions).
  const userSelectedMetric = useRef(false);

  // This card always breaks down by publisher, never by platform, so the only
  // way a row is one DSP's is a single-platform filter. Otherwise the column
  // would be conversions summed across platforms that count them differently
  // and share no pixel (#4477).
  const perPlatform = isConversionsPerPlatformScope(filters.dsps, ['publisher']);
  const metricOptions = buildMetricOptions(permissions, perPlatform);

  // Widening the filter while Conversions is selected would keep querying and
  // charting it behind a toggle that no longer offers it.
  useEffect(() => {
    if (!perPlatform && metric === 'conversions') {
      setMetric('impressions');
      setPage(0);
    }
  }, [perPlatform, metric]);

  // Re-applies the permission default once restrictedFields has actually
  // loaded (useReportingPermissions optimistically assumes Client Spend is
  // allowed until then). A manual selection survives only if it's still
  // permitted under the resolved permissions — see reconcileMetric.
  useEffect(() => {
    setMetric(prev => reconcileMetric(prev, userSelectedMetric.current, permissions, perPlatform));
  }, [permissions.canViewWholesaleSpend, permissions.canViewClientSpend, permissions.hasClientSpendData, perPlatform]);

  // client_spend/client_cpm can't be the server sort field (see isComputedMetric
  // above), so rank by impressions instead and widen the pool — a much higher
  // limit than the usual 100, since a client-spend/CPM leader could easily sit
  // outside the top 100 by impressions. Not an exact ranking: a publisher outside
  // the top 1000 by impressions (the server's max allowed limit) that would
  // otherwise lead by Client Spend is still missed, but in practice publisher
  // counts per filter are well under that.
  const computedMetric = isComputedMetric(metric);
  const {
    rows: fetchedRows,
    loading,
    error,
  } = useReportWidget(KEY, {
    dimensions: [PUBLISHER_DIMENSION],
    metrics: [metric],
    sort: [{ field: computedMetric ? 'impressions' : metric, dir: 'desc' }],
    limit: computedMetric ? 1000 : 100,
  });
  const rows = computedMetric ? sortByMetricDesc(fetchedRows, metric) : fetchedRows;
  const validRows = chartablePublisherRows(rows);
  const onExportCsv = useExportWidgetCsv('topPublishersMetric', toExportRows(rows));
  const totalPages = Math.max(1, Math.ceil(validRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pageRows = validRows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const labels = pageRows.map(r => String(r.ribeye_publisher_name ?? ''));
  const values = pageRows.map(r => Number(r[metric] ?? 0));

  const allNull = rows.length > 0 && validRows.length === 0;

  const handleMetricChange = (next: string) => {
    userSelectedMetric.current = true;
    setMetric(next);
    setPage(0);
  };

  return (
    <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          Top Publishers
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MetricToggle options={metricOptions} value={metric} onChange={handleMetricChange} />
          <WidgetExportButton onExport={onExportCsv} disabled={validRows.length === 0} />
        </Box>
      </Box>

      {loading ? (
        <WidgetSkeleton height={280} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : allNull ? (
        <Typography variant='body2' color='text.secondary'>
          Publisher data not yet available — mapping backfill in progress.
        </Typography>
      ) : validRows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No data for the selected range yet.
        </Typography>
      ) : (
        <>
          <BarChart
            labels={labels}
            values={values}
            format={v => formatValue(metric, v)}
            tickFormat={v => formatTick(metric, v)}
            height={280}
          />

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
          >
            <Typography variant='caption' sx={{ color: COLORS.muted }}>
              Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, validRows.length)} of{' '}
              {validRows.length}
            </Typography>
            <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size='small'
                disabled={safePage === 0}
                onClick={() => setPage(p => p - 1)}
                sx={{ minWidth: 0, p: 0.5, color: COLORS.muted }}
              >
                <ChevronLeftRounded />
              </Button>
              <Typography variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                {safePage + 1} / {totalPages}
              </Typography>
              <Button
                size='small'
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                sx={{ minWidth: 0, p: 0.5, color: COLORS.muted }}
              >
                <ChevronRightRounded />
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TopPublishersCard;
